import type { DailyRewardPreview, DailyRewardStatus } from "../types";
import { FormattedNumber } from "./FormattedNumber";
import { ModalBackdrop } from "./ModalBackdrop";

interface DailyRewardModalProps {
  status: DailyRewardStatus;
  claiming: boolean;
  claimed: boolean;
  claimSummary: string | null;
  onClaim: () => void;
  onClose: () => void;
}

export function DailyRewardModal({
  status,
  claiming,
  claimed,
  claimSummary,
  onClaim,
  onClose,
}: DailyRewardModalProps) {
  const reward = status.today_reward;
  const progressPct = Math.min(100, (status.streak_day / status.max_day) * 100);

  return (
    <ModalBackdrop onClose={onClose} className="z-50 items-center bg-black/70 px-4 py-6">
      <div className="w-full max-w-sm max-h-[90vh] flex flex-col rounded-2xl bg-kingdom-purple border-2 border-kingdom-glow/50 shadow-[0_0_40px_rgba(139,92,246,0.25)] overflow-hidden">
        <div className="p-5 pb-3 text-center space-y-3 shrink-0">
          <div className="text-5xl">{claimed ? "🎁" : "📅"}</div>
          <div>
            <h2 className="text-xl font-bold font-game text-gold-400">
              {claimed ? "Reward Claimed!" : "Daily Login Reward"}
            </h2>
            {!claimed && (
              <p className="text-gray-400 text-sm mt-1">
                Day <span className="text-white font-bold">{status.streak_day}</span> of{" "}
                <span className="text-white font-bold">{status.max_day}</span>
                {status.streak_broken && (
                  <span className="block text-amber-300 mt-1">Streak reset — start again from day 1.</span>
                )}
              </p>
            )}
            {claimed && claimSummary && (
              <p className="text-gray-300 text-sm mt-1">{claimSummary}</p>
            )}
          </div>

          {!claimed && (
            <div className="space-y-1.5 text-left">
              <div className="flex justify-between text-xs text-gray-400">
                <span>Login streak</span>
                <span>
                  Day {status.streak_day} / {status.max_day}
                </span>
              </div>
              <div className="h-2.5 bg-kingdom-dark rounded-full overflow-hidden border border-kingdom-mid">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-kingdom-accent to-kingdom-glow"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 space-y-3">
          <RewardCard
            reward={reward}
            label={claimed ? "Claimed today" : "Today — claim now"}
            highlight
          />

          {!claimed && status.newbie_bonus_today && status.newbie_days_remaining ? (
            <div className="rounded-xl p-3 text-left bg-emerald-950/40 border border-emerald-600/40 space-y-1">
              <div className="text-xs font-bold text-emerald-400 uppercase tracking-wider">
                New hero bonus — {status.newbie_days_remaining} day
                {status.newbie_days_remaining === 1 ? "" : "s"} left
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm">
                {status.newbie_bonus_today.gold > 0 && (
                  <span className="text-gold-300 font-semibold">
                    🪙 +<FormattedNumber value={status.newbie_bonus_today.gold} />
                  </span>
                )}
                {status.newbie_bonus_today.diamonds > 0 && (
                  <span className="text-cyan-300 font-semibold">
                    💎 +<FormattedNumber value={status.newbie_bonus_today.diamonds} />
                  </span>
                )}
              </div>
              <div className="text-[11px] text-gray-500">Added on top of today&apos;s login reward.</div>
            </div>
          ) : null}

          {status.upcoming_rewards.length > 0 && (
            <div className="space-y-2 pb-1">
              <div className="text-xs font-bold text-gray-400 uppercase tracking-wider text-left">
                Coming up — keep your streak
              </div>
              {status.upcoming_rewards.map((upcoming) => (
                <RewardCard
                  key={upcoming.day}
                  reward={upcoming}
                  label={`Day ${upcoming.day}`}
                />
              ))}
            </div>
          )}
        </div>

        <div className="p-5 pt-3 shrink-0 border-t border-kingdom-mid/50">
          {!claimed ? (
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 py-3 rounded-xl border border-kingdom-light text-gray-400 font-semibold"
              >
                Later
              </button>
              <button
                onClick={onClaim}
                disabled={claiming || !status.can_claim}
                className="flex-[2] py-3 rounded-xl bg-gradient-to-r from-kingdom-accent to-kingdom-glow text-white font-bold disabled:opacity-60"
              >
                {claiming ? "Claiming…" : "Claim Reward"}
              </button>
            </div>
          ) : (
            <button
              onClick={onClose}
              className="w-full py-3 rounded-xl bg-kingdom-mid text-white font-bold"
            >
              Continue
            </button>
          )}
        </div>
      </div>
    </ModalBackdrop>
  );
}

function RewardCard({
  reward,
  label,
  highlight = false,
}: {
  reward: DailyRewardPreview;
  label: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={`rounded-xl p-3 text-left space-y-1.5 ${
        highlight
          ? "bg-kingdom-dark border-2 border-gold-500/60 shadow-[0_0_12px_rgba(251,191,36,0.15)]"
          : "bg-kingdom-dark/70 border border-kingdom-mid"
      }`}
    >
      <div className={`text-xs font-bold ${highlight ? "text-gold-400" : "text-gray-400"}`}>
        {label}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm">
        {reward.gold > 0 && (
          <span className="text-gold-300 font-semibold">
            🪙 <FormattedNumber value={reward.gold} />
          </span>
        )}
        {reward.diamonds > 0 && (
          <span className="text-cyan-300 font-semibold">
            💎 <FormattedNumber value={reward.diamonds} />
          </span>
        )}
        {reward.consumable_item_id && reward.consumable_quantity > 0 && (
          <span className="text-emerald-300">
            🧪 {formatConsumable(reward.consumable_item_id)} ×{reward.consumable_quantity}
          </span>
        )}
        {reward.item_template_name && (
          <span className="text-purple-300">🎒 {reward.item_template_name}</span>
        )}
      </div>
      {reward.description && (
        <div className="text-[11px] text-gray-500 leading-snug">{reward.description}</div>
      )}
    </div>
  );
}

function formatConsumable(id: string): string {
  return id.replace(/_/g, " ");
}
